import { z } from "zod";

import { blockVisibilitySchema, visibilityConditionSchema } from "./visibility";
import { i18n } from "@tellescope/i18n";

// Grid column values (1-12 for 12-column grid system)
const gridColumnSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
  z.literal(8),
  z.literal(9),
  z.literal(10),
  z.literal(11),
  z.literal(12),
]);

export type GridColumn = z.infer<typeof gridColumnSchema>;

// Valid grid column values for type guard
const VALID_GRID_COLUMNS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

/**
 * Type guard to check if a number is a valid GridColumn (1-12).
 */
export function isGridColumn(value: number): value is GridColumn {
  return VALID_GRID_COLUMNS.includes(value as GridColumn);
}

// Responsive layout schema for block widths at different breakpoints
// Uses Tailwind's breakpoint system: xs (default), sm (640px), md (768px), lg (1024px), xl (1280px)
export const responsiveLayoutSchema = z.object({
  xs: gridColumnSchema.default(12), // Mobile first - default full width
  sm: gridColumnSchema.optional(), // >= 640px
  md: gridColumnSchema.optional(), // >= 768px
  lg: gridColumnSchema.optional(), // >= 1024px
  xl: gridColumnSchema.optional(), // >= 1280px
});

export type ResponsiveLayout = z.infer<typeof responsiveLayoutSchema>;

// Per-block style overrides — all optional, applied on top of portal-level theming
export const blockStyleSchema = z.object({
  padding: z.enum(["none", "sm", "md", "lg"]).optional(),
  textAlign: z.enum(["left", "center", "right"]).optional(),
  textColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a valid hex color")
    .optional(),
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a valid hex color")
    .optional(),
});

export type BlockStyle = z.infer<typeof blockStyleSchema>;

// Base block schema with common fields
const baseBlockSchema = z.object({
  id: z.string().uuid(),
  layout: responsiveLayoutSchema.optional(),
  visibility: blockVisibilitySchema.optional(), // Conditional visibility (all blocks inherit this)
  style: blockStyleSchema.optional(),
});

// Item filter schema - client-side filtering using visibility conditions
// Applied after data is loaded, filters items based on condition evaluation
// Uses the same condition format as block visibility for consistency
export const itemFilterSchema = z.object({
  condition: visibilityConditionSchema,
  description: z.string().optional(), // Admin UI description for the filter
});

export type ItemFilter = z.infer<typeof itemFilterSchema>;

// Text block - for body text content
export const textBlockSchema = baseBlockSchema.extend({
  type: z.literal("text"),
  props: z.object({
    content: z.string(),
    variant: z.enum(["body", "caption", "muted"]).default("body"),
  }),
});

// Heading block - for section headers
export const headingBlockSchema = baseBlockSchema.extend({
  type: z.literal("heading"),
  props: z.object({
    text: z.string(),
    level: z.enum(["h1", "h2", "h3", "h4"]).default("h2"),
  }),
});

// Button block - for CTAs and links
export const buttonBlockSchema = baseBlockSchema.extend({
  type: z.literal("button"),
  props: z.object({
    text: z.string(),
    variant: z.enum(["default", "secondary", "outline", "ghost"]).default("default"),
    href: z.string().optional(),
  }),
});

// Card block - for grouped content
export const cardBlockSchema = baseBlockSchema.extend({
  type: z.literal("card"),
  props: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
  }),
});

// Spacer block - for vertical spacing
export const spacerBlockSchema = baseBlockSchema.extend({
  type: z.literal("spacer"),
  props: z.object({
    size: z.enum(["sm", "md", "lg", "xl"]).default("md"),
  }),
});

// Divider block - for horizontal dividers
export const dividerBlockSchema = baseBlockSchema.extend({
  type: z.literal("divider"),
  props: z.object({}),
});

// Banner block - for announcements with WYSIWYG content
export const BANNER_CUSTOM_ICONS = [
  "info",
  "check-circle",
  "alert-triangle",
  "x-circle",
  "bell",
  "star",
  "heart",
  "zap",
  "shield",
  "message-circle",
] as const;
export type BannerCustomIcon = (typeof BANNER_CUSTOM_ICONS)[number];

export const bannerBlockSchema = baseBlockSchema.extend({
  type: z.literal("banner"),
  props: z.object({
    content: z.string().default(""),
    variant: z.enum(["info", "success", "warning", "error", "neutral", "custom"]).default("info"),
    dismissible: z.boolean().default(false),
    // Only used when variant === "custom"
    customBackgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    customTextColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    customIcon: z.enum(BANNER_CUSTOM_ICONS).optional(),
  }),
});

// HTML block - for custom HTML content
export const htmlBlockSchema = baseBlockSchema.extend({
  type: z.literal("html"),
  props: z.object({
    content: z.string().default(""), // Raw HTML content
  }),
});

// Rich text block - for WYSIWYG formatted content
export const richTextBlockSchema = baseBlockSchema.extend({
  type: z.literal("rich-text"),
  props: z.object({
    content: z.string().default(""), // HTML content from WYSIWYG editor
  }),
});

// Popup dismissal behavior
export const popupDismissalBehaviorSchema = z.enum([
  "always", // Shows every page load (session-only dismiss)
  "period", // Shows again after configured duration
  "forever", // Never shows again once dismissed
]);
export type PopupDismissalBehavior = z.infer<typeof popupDismissalBehaviorSchema>;

// Popup dismissal period
export const popupDismissalPeriodSchema = z.enum(["1h", "1d", "7d", "30d"]);
export type PopupDismissalPeriod = z.infer<typeof popupDismissalPeriodSchema>;

// Popup block - modal that shows on page load
export const popupBlockSchema = baseBlockSchema.extend({
  type: z.literal("popup"),
  props: z.object({
    content: z.string().default(""), // HTML content from WYSIWYG editor
    variant: z.enum(["info", "success", "warning", "error", "neutral", "custom"]).default("info"),
    customBackgroundColor: z.string().optional(), // Hex color for custom variant
    customTextColor: z.string().optional(), // Hex color for custom variant text
    title: z.string().optional(), // Optional popup title
    size: z.enum(["default", "wide"]).default("default"),
    dismissalBehavior: popupDismissalBehaviorSchema.default("always"),
    dismissalPeriod: popupDismissalPeriodSchema.optional(), // Required when behavior is "period"
    showOnAllPages: z.boolean().default(false), // When true, shows on all pages
  }),
});

// Appointment category type
export const appointmentCategorySchema = z.enum(["upcoming", "past", "all"]);
export type AppointmentCategory = z.infer<typeof appointmentCategorySchema>;

// Appointment list block - shows appointments with optional category filtering
export const appointmentListBlockSchema = baseBlockSchema.extend({
  type: z.literal("appointment-list"),
  props: z.object({
    maxItems: z.number().min(1).max(10).default(3),
    emptyMessage: z.string().optional(),
    // Categories to show. If multiple provided, shows a selector.
    // Default is ["all"] which shows all appointments.
    categories: z.array(appointmentCategorySchema).min(1).default(["all"]),
    // Client-side item filter
    itemFilter: itemFilterSchema.optional(),
    // Cancel/reschedule settings
    allowCancel: z.boolean().default(false),
    // Custom reschedule URL template. Supports {{enduser.fname}}, {{snippet.key}}, etc.
    // When configured, shows a Reschedule button after cancellation.
    rescheduleUrl: z.string().optional(),
    // Map appointment title to custom reschedule URL. Takes precedence over rescheduleUrl.
    rescheduleUrlOverrides: z.record(z.string(), z.string()).optional(),
    // Minutes after appointment end time to hide the video call button. When unset, the button is always shown.
    hideVideoAfterMinutes: z.number().min(0).optional(),
  }),
});

// Order list block - shows enduser orders
export const orderListBlockSchema = baseBlockSchema.extend({
  type: z.literal("order-list"),
  props: z.object({
    maxItems: z.number().min(1).max(10).default(5),
    emptyMessage: z.string().optional(),
    // Custom tracking URL template. Use {{tracking}} as placeholder.
    // Example: "https://ups.com/track?num={{tracking}}"
    // If not set, defaults to Google search.
    customTrackingUrl: z.string().optional(),
    // Client-side item filter
    itemFilter: itemFilterSchema.optional(),
  }),
});

// Chat room list block - shows enduser chat rooms/messages
export const chatRoomListBlockSchema = baseBlockSchema.extend({
  type: z.literal("chatroom-list"),
  props: z.object({
    maxItems: z.number().min(1).max(10).default(5),
    emptyMessage: z.string().optional(),
    allowNewConversation: z.boolean().default(false),
    allowCareTeamSelection: z.boolean().optional(),
    // Client-side item filter
    itemFilter: itemFilterSchema.optional(),
  }),
});

// File list block - shows enduser files
export const fileListBlockSchema = baseBlockSchema.extend({
  type: z.literal("file-list"),
  props: z.object({
    maxItems: z.number().min(1).max(20).default(10),
    emptyMessage: z.string().optional(),
    // Client-side item filter
    itemFilter: itemFilterSchema.optional(),
  }),
});

// Form response category type
export const formResponseCategorySchema = z.enum(["submitted", "unsubmitted", "all"]);
export type FormResponseCategory = z.infer<typeof formResponseCategorySchema>;

// Form response list block - shows form responses with category filtering
export const formResponseListBlockSchema = baseBlockSchema.extend({
  type: z.literal("form-response-list"),
  props: z.object({
    maxItems: z.number().min(1).max(20).default(5),
    emptyMessage: z.string().optional(),
    // Categories to show. If multiple provided, shows a selector.
    // Default is ["all"] which shows all visible form responses.
    categories: z.array(formResponseCategorySchema).min(1).default(["all"]),
    // Client-side item filter
    itemFilter: itemFilterSchema.optional(),
    // Map original form titles to custom display titles
    titleOverrides: z.record(z.string(), z.string()).optional(),
  }),
});

// Pinned forms list block - shows forms available for portal submission
// Automatically displays all forms with allowPortalSubmission: true
// Filters by allowPortalSubmissionEnduserCondition if specified on the form
export const pinnedFormsListBlockSchema = baseBlockSchema.extend({
  type: z.literal("pinned-forms-list"),
  props: z.object({
    maxItems: z.number().min(1).max(20).default(4),
    // Optional title override
    title: z.string().optional(),
    emptyMessage: z.string().optional(),
    // Client-side item filter - filters forms based on visibility conditions
    // Evaluated per-form with form context (form.title, form.id, etc.)
    itemFilter: itemFilterSchema.optional(),
  }),
});

// Profile field keys for configuration
export const profileFieldKeySchema = z.enum([
  "fname",
  "lname",
  "email",
  "phone",
  "addressLineOne",
  "addressLineTwo",
  "city",
  "state",
  "zipCode",
]);
export type ProfileFieldKey = z.infer<typeof profileFieldKeySchema>;

// Configuration for a single profile field
export const profileFieldConfigSchema = z.object({
  visible: z.boolean().default(true),
  editable: z.boolean().default(true),
  required: z.boolean().default(true), // Default to required per user preference
  label: z.string().optional(), // Custom label override
});
export type ProfileFieldConfig = z.infer<typeof profileFieldConfigSchema>;

// Profile edit block - allows enduser to edit their profile information
export const profileEditBlockSchema = baseBlockSchema.extend({
  type: z.literal("profile-edit"),
  props: z.object({
    // Field configurations - keyed by field name
    // Fields not specified use defaults (visible: true, editable: true, required: true)
    // Uses .partial() so we only store non-default values
    fields: z.record(profileFieldKeySchema, profileFieldConfigSchema.partial()).default({}),
    // Form-level settings
    title: z.string().optional(),
    submitButtonText: z.string().default("Save Changes"),
    successMessage: z.string().default("Profile updated successfully"),
    // Whether to show the reset button (defaults to false)
    showResetButton: z.boolean().default(false),
  }),
});

// Content list block - shows managed content records assigned to the enduser
// Backend handles all filtering (by assignment, category, visibility)
export const contentListBlockSchema = baseBlockSchema.extend({
  type: z.literal("content-list"),
  props: z.object({
    maxItems: z.number().min(1).max(50).default(10),
    emptyMessage: z.string().optional(),
    title: z.string().optional(),
    headerPhotoStyle: z.enum(["banner", "icon"]).default("banner"),
    showSearch: z.boolean().default(false),
    // Client-side item filter
    itemFilter: itemFilterSchema.optional(),
  }),
});

// Purchase list block - shows enduser purchases/payments
export const purchaseListBlockSchema = baseBlockSchema.extend({
  type: z.literal("purchase-list"),
  props: z.object({
    maxItems: z.number().min(1).max(20).default(5),
    emptyMessage: z.string().optional(),
    // Client-side item filter
    itemFilter: itemFilterSchema.optional(),
  }),
});

// Appointment booking block - shows available booking pages for scheduling appointments
export const appointmentBookingBlockSchema = baseBlockSchema.extend({
  type: z.literal("appointment-booking"),
  props: z.object({
    maxItems: z.number().min(1).max(50).default(5),
    emptyMessage: z.string().optional(),
    // Client-side item filter
    itemFilter: itemFilterSchema.optional(),
  }),
});

// File upload block - button to upload files associated with enduser
export const fileUploadBlockSchema = baseBlockSchema.extend({
  type: z.literal("file-upload"),
  props: z.object({
    variant: z.enum(["button", "icon"]).default("button"),
    buttonVariant: z.enum(["default", "secondary", "outline", "ghost"]).default("default"), // Only for button variant
    text: z.string().default("Upload File"), // Button text or tooltip
    acceptedFileTypes: z.string().optional(), // e.g., "image/*,.pdf" - maps to <input accept="...">
    maxFileSizeMB: z.number().min(1).max(100).optional(), // Frontend validation
  }),
});

// Cancel iframe configuration - subset of iframe block props for embedding in cancel dialog
// Height uses same preset sizes as iframe block: sm=200px, md=400px, lg=600px, xl=800px
export const cancelIframeConfigSchema = z.object({
  url: z.string(),
  height: z.union([z.number().min(100).max(2000), z.enum(["sm", "md", "lg", "xl"])]).default("md"),
  title: z.string().optional(),
  passToken: z.boolean().default(true),
  tokenParam: z.string().default("token"),
  subscriptionIdParam: z.string().default("subscriptionId"),
});

export type CancelIframeConfig = z.infer<typeof cancelIframeConfigSchema>;

// Pause iframe configuration - subset of iframe block props for embedding in pause dialog
// Height uses same preset sizes as iframe block: sm=200px, md=400px, lg=600px, xl=800px
export const pauseIframeConfigSchema = z.object({
  url: z.string(),
  height: z.union([z.number().min(100).max(2000), z.enum(["sm", "md", "lg", "xl"])]).default("md"),
  title: z.string().optional(),
  passToken: z.boolean().default(true),
  tokenParam: z.string().default("token"),
  subscriptionIdParam: z.string().default("subscriptionId"),
});

export type PauseIframeConfig = z.infer<typeof pauseIframeConfigSchema>;

// Stripe subscriptions block - displays Stripe subscriptions via integrations.proxy_read
// Subscription management actions are all disabled by default and must be enabled via block props
export const stripeSubscriptionsBlockSchema = baseBlockSchema.extend({
  type: z.literal("stripe-subscriptions"),
  props: z.object({
    maxItems: z.number().min(1).max(20).default(5),
    emptyMessage: z.string().optional(),
    // Subscription management actions - all disabled by default
    allowCancel: z.boolean().default(false),
    allowUndoCancel: z.boolean().default(false),
    allowPause: z.boolean().default(false),
    allowResume: z.boolean().default(false),
    allowUpdatePaymentMethod: z.boolean().default(false),
    // Optional iframe config for custom cancel flow - when set, shows iframe instead of built-in cancel dialog
    cancelIframe: cancelIframeConfigSchema.optional(),
    // Optional iframe config for custom pause flow - when set, shows iframe instead of built-in pause dialog
    pauseIframe: pauseIframeConfigSchema.optional(),
    itemFilter: itemFilterSchema.optional(),
  }),
});

// Stripe billing portal button block - redirects endusers to the Stripe self-serve billing portal
// Renders nothing if the enduser does not have a stripeCustomerId set
export const stripeBillingPortalButtonBlockSchema = baseBlockSchema.extend({
  type: z.literal("stripe-billing-portal-button"),
  props: z.object({
    label: z.string().default("Manage Subscriptions"),
    variant: z.enum(["default", "outline", "ghost"]).default("default"),
  }),
});

// Stripe invoices block - displays Stripe invoice history via integrations.proxy_read
// Read-only: no mutations needed as invoices cannot be modified from the portal
export const stripeInvoicesBlockSchema = baseBlockSchema.extend({
  type: z.literal("stripe-invoices"),
  props: z.object({
    maxItems: z.number().min(1).max(20).default(5),
    emptyMessage: z.string().optional(),
    itemFilter: itemFilterSchema.optional(),
  }),
});

// Vital data block - displays lab results and vital sign readings from EnduserObservations
export const vitalDataBlockSchema = baseBlockSchema.extend({
  type: z.literal("vital-data"),
  props: z.object({
    emptyMessage: z.string().optional(),
    itemFilter: itemFilterSchema.optional(),
  }),
});

// Weight & macro tracker block - lets endusers log weight, food/macros, and water,
// and shows progress against their personal daily targets plus a trend chart over time.
// Targets and logged entries both live on EnduserObservation/Enduser directly, so props stay thin.
export const weightMacroTrackerBlockSchema = baseBlockSchema.extend({
  type: z.literal("weight-macro-tracker"),
  props: z.object({
    emptyMessage: z.string().optional(),
  }),
});

// Non-container block types (blocks that cannot contain other blocks)
// This is used to define what can be nested inside a Section
const leafBlockSchema = z.discriminatedUnion("type", [
  textBlockSchema,
  headingBlockSchema,
  buttonBlockSchema,
  cardBlockSchema,
  spacerBlockSchema,
  dividerBlockSchema,
  bannerBlockSchema,
  htmlBlockSchema,
  richTextBlockSchema,
  popupBlockSchema,
  appointmentListBlockSchema,
  appointmentBookingBlockSchema,
  orderListBlockSchema,
  chatRoomListBlockSchema,
  fileListBlockSchema,
  formResponseListBlockSchema,
  pinnedFormsListBlockSchema,
  profileEditBlockSchema,
  contentListBlockSchema,
  purchaseListBlockSchema,
  fileUploadBlockSchema,
  stripeSubscriptionsBlockSchema,
  stripeBillingPortalButtonBlockSchema,
  stripeInvoicesBlockSchema,
  vitalDataBlockSchema,
  weightMacroTrackerBlockSchema,
]);

export type LeafBlock = z.infer<typeof leafBlockSchema>;

// Type helper to extract a specific leaf block type by its type discriminant
export type LeafBlockByType<T extends LeafBlock["type"]> = Extract<LeafBlock, { type: T }>;

// Type-safe updates for a leaf block - requires type field for verification
export type LeafBlockUpdate<T extends LeafBlock["type"]> = {
  type: T;
  props?: Partial<LeafBlockByType<T>["props"]>;
} & Partial<Omit<LeafBlockByType<T>, "type" | "id" | "props">>;

// Type helper to extract a specific block type by its type discriminant (includes section)
export type BlockByType<T extends Block["type"]> = Extract<Block, { type: T }>;

// Type-safe updates for any block - requires type field for verification
export type BlockUpdate<T extends Block["type"]> = {
  type: T;
  props?: Partial<BlockByType<T>["props"]>;
} & Partial<Omit<BlockByType<T>, "type" | "id" | "props">>;

// Container block types - blocks that can contain other blocks
// These cannot be nested inside other container blocks
export const CONTAINER_BLOCK_TYPES = ["section", "iframe"] as const;
export type ContainerBlockType = (typeof CONTAINER_BLOCK_TYPES)[number];

// Type guard to check if a block type is a container block
export const isContainerBlockType = (type: string): type is ContainerBlockType => {
  return CONTAINER_BLOCK_TYPES.includes(type as ContainerBlockType);
};

// Type guard to check if a block type is a leaf block (not a container)
export const isLeafBlockType = (type: string): type is LeafBlock["type"] => {
  return !CONTAINER_BLOCK_TYPES.includes(type as ContainerBlockType);
};

// Section block - a container that groups blocks together
// Child blocks share the section's layout (width) and stack vertically within it
export const sectionBlockSchema = baseBlockSchema.extend({
  type: z.literal("section"),
  props: z.object({
    // Optional title for the section (can be used for accessibility or visual headers)
    title: z.string().optional(),
    // Gap between child blocks: sm=8px, md=16px (default), lg=24px
    gap: z.enum(["sm", "md", "lg"]).default("md"),
  }),
  // Child blocks - cannot contain other sections (no deep nesting)
  blocks: z.array(leafBlockSchema).default([]),
});

// Iframe sandbox options
export const iframeSandboxSchema = z.object({
  allowScripts: z.boolean().default(true),
  allowSameOrigin: z.boolean().default(true),
  allowForms: z.boolean().default(true),
  allowPopups: z.boolean().default(false),
});

export type IframeSandbox = z.infer<typeof iframeSandboxSchema>;

// Iframe height - preset sizes or custom pixel value
export const iframeHeightSchema = z.union([z.number().min(100).max(2000), z.enum(["sm", "md", "lg", "xl"])]);

export type IframeHeight = z.infer<typeof iframeHeightSchema>;

// Iframe block - embeds external content with token passthrough and fallback support
// This is a container block that can hold fallback blocks shown when iframe signals an error
export const iframeBlockSchema = baseBlockSchema.extend({
  type: z.literal("iframe"),
  props: z.object({
    // The base URL to embed (token will be appended as query param)
    url: z.string(),
    // Height: preset sizes (sm=200px, md=400px, lg=600px, xl=800px) or custom pixels
    height: iframeHeightSchema.default("md"),
    // Title for accessibility (iframe title attribute)
    title: z.string().optional(),
    // Whether to pass authentication token as URL parameter (default: true)
    passToken: z.boolean().default(true),
    // Token parameter name (default: "token")
    tokenParam: z.string().default("token"),
    // Sandbox security options
    sandbox: iframeSandboxSchema.default({}),
  }),
  // Fallback blocks - rendered when iframe sends error postMessage
  // Embedded page triggers fallback via: window.parent.postMessage({ type: 'iframe-error' }, '*')
  fallbackBlocks: z.array(leafBlockSchema).default([]),
});

// Discriminated union of all block types (including section and iframe)
export const blockSchema = z.discriminatedUnion("type", [
  textBlockSchema,
  headingBlockSchema,
  buttonBlockSchema,
  cardBlockSchema,
  spacerBlockSchema,
  dividerBlockSchema,
  bannerBlockSchema,
  htmlBlockSchema,
  richTextBlockSchema,
  popupBlockSchema,
  appointmentListBlockSchema,
  appointmentBookingBlockSchema,
  orderListBlockSchema,
  chatRoomListBlockSchema,
  fileListBlockSchema,
  formResponseListBlockSchema,
  pinnedFormsListBlockSchema,
  profileEditBlockSchema,
  contentListBlockSchema,
  purchaseListBlockSchema,
  fileUploadBlockSchema,
  stripeSubscriptionsBlockSchema,
  stripeBillingPortalButtonBlockSchema,
  stripeInvoicesBlockSchema,
  vitalDataBlockSchema,
  weightMacroTrackerBlockSchema,
  sectionBlockSchema,
  iframeBlockSchema,
]);

// Inferred types from schemas
export type TextBlock = z.infer<typeof textBlockSchema>;
export type HeadingBlock = z.infer<typeof headingBlockSchema>;
export type ButtonBlock = z.infer<typeof buttonBlockSchema>;
export type CardBlock = z.infer<typeof cardBlockSchema>;
export type SpacerBlock = z.infer<typeof spacerBlockSchema>;
export type DividerBlock = z.infer<typeof dividerBlockSchema>;
export type BannerBlock = z.infer<typeof bannerBlockSchema>;
export type HtmlBlock = z.infer<typeof htmlBlockSchema>;
export type RichTextBlock = z.infer<typeof richTextBlockSchema>;
export type PopupBlock = z.infer<typeof popupBlockSchema>;
export type AppointmentListBlock = z.infer<typeof appointmentListBlockSchema>;
export type AppointmentBookingBlock = z.infer<typeof appointmentBookingBlockSchema>;
export type OrderListBlock = z.infer<typeof orderListBlockSchema>;
export type ChatRoomListBlock = z.infer<typeof chatRoomListBlockSchema>;
export type FileListBlock = z.infer<typeof fileListBlockSchema>;
export type FormResponseListBlock = z.infer<typeof formResponseListBlockSchema>;
export type PinnedFormsListBlock = z.infer<typeof pinnedFormsListBlockSchema>;
export type ProfileEditBlock = z.infer<typeof profileEditBlockSchema>;
export type ContentListBlock = z.infer<typeof contentListBlockSchema>;
export type PurchaseListBlock = z.infer<typeof purchaseListBlockSchema>;
export type FileUploadBlock = z.infer<typeof fileUploadBlockSchema>;
export type StripeSubscriptionsBlock = z.infer<typeof stripeSubscriptionsBlockSchema>;
export type StripeBillingPortalButtonBlock = z.infer<typeof stripeBillingPortalButtonBlockSchema>;
export type StripeInvoicesBlock = z.infer<typeof stripeInvoicesBlockSchema>;
export type VitalDataBlock = z.infer<typeof vitalDataBlockSchema>;
export type WeightMacroTrackerBlock = z.infer<typeof weightMacroTrackerBlockSchema>;
export type SectionBlock = z.infer<typeof sectionBlockSchema>;
export type IframeBlock = z.infer<typeof iframeBlockSchema>;
export type Block = z.infer<typeof blockSchema>;
export type BlockType = Block["type"];

// Block categories for UI grouping
export type BlockCategory = "content" | "layout" | "data" | "integrations";

export const getBlockCategories: () => { category: BlockCategory; label: string }[] = () => [
  { category: "content", label: i18n.t("Content", { ns: "common" }) },
  { category: "layout", label: i18n.t("Layout", { ns: "common" }) },
  { category: "data", label: i18n.t("Data", { ns: "common" }) },
  { category: "integrations", label: i18n.t("Integrations", { ns: "common" }) },
];

// Block type list for UI
export const getBlockTypes: () => {
  type: BlockType;
  label: string;
  description: string;
  category: BlockCategory;
}[] = () => [
  // Content blocks
  {
    type: "heading",
    label: i18n.t("Heading", { ns: "common" }),
    description: i18n.t("Section title or header", { ns: "common" }),
    category: "content",
  },
  {
    type: "text",
    label: i18n.t("Text", { ns: "common" }),
    description: i18n.t("Body text content", { ns: "common" }),
    category: "content",
  },
  {
    type: "button",
    label: i18n.t("Button", { ns: "common" }),
    description: i18n.t("Call-to-action button", { ns: "common" }),
    category: "content",
  },
  {
    type: "card",
    label: i18n.t("Card", { ns: "common" }),
    description: i18n.t("Grouped content container", { ns: "common" }),
    category: "content",
  },
  {
    type: "banner",
    label: i18n.t("Banner", { ns: "common" }),
    description: i18n.t("Announcement with rich text content", { ns: "common" }),
    category: "content",
  },
  {
    type: "html",
    label: i18n.t("HTML", { ns: "common" }),
    description: i18n.t("Custom HTML content", { ns: "common" }),
    category: "content",
  },
  {
    type: "rich-text",
    label: i18n.t("Rich Text", { ns: "common" }),
    description: i18n.t("Formatted text content", { ns: "common" }),
    category: "content",
  },
  {
    type: "popup",
    label: i18n.t("Popup", { ns: "common" }),
    description: i18n.t("Modal announcement on page load", { ns: "common" }),
    category: "content",
  },
  // Layout blocks
  {
    type: "section",
    label: i18n.t("Section", { ns: "common" }),
    description: i18n.t("Group blocks together with shared layout", { ns: "common" }),
    category: "layout",
  },
  {
    type: "spacer",
    label: i18n.t("Spacer", { ns: "common" }),
    description: i18n.t("Vertical spacing", { ns: "common" }),
    category: "layout",
  },
  {
    type: "divider",
    label: i18n.t("Divider", { ns: "common" }),
    description: i18n.t("Horizontal line separator", { ns: "common" }),
    category: "layout",
  },
  // Data blocks
  {
    type: "appointment-list",
    label: i18n.t("Appointments", { ns: "common" }),
    description: i18n.t("List of appointments", { ns: "common" }),
    category: "data",
  },
  {
    type: "appointment-booking",
    label: i18n.t("Book Appointment", { ns: "common" }),
    description: i18n.t("List of available booking pages for scheduling", { ns: "common" }),
    category: "data",
  },
  {
    type: "order-list",
    label: i18n.t("Orders", { ns: "common" }),
    description: i18n.t("List of enduser orders", { ns: "common" }),
    category: "data",
  },
  {
    type: "chatroom-list",
    label: i18n.t("Messages", { ns: "common" }),
    description: i18n.t("List of chat conversations", { ns: "common" }),
    category: "data",
  },
  {
    type: "file-list",
    label: i18n.t("Files", { ns: "common" }),
    description: i18n.t("List of patient documents and files", { ns: "common" }),
    category: "data",
  },
  {
    type: "form-response-list",
    label: i18n.t("Form Responses", { ns: "common" }),
    description: i18n.t("List of form responses", { ns: "common" }),
    category: "data",
  },
  {
    type: "pinned-forms-list",
    label: i18n.t("Forms", { ns: "common" }),
    description: i18n.t("Form templates available for submission", { ns: "common" }),
    category: "data",
  },
  {
    type: "profile-edit",
    label: i18n.t("Profile Edit", { ns: "common" }),
    description: i18n.t("Editable profile form for endusers", { ns: "common" }),
    category: "data",
  },
  {
    type: "content-list",
    label: i18n.t("Content", { ns: "common" }),
    description: i18n.t("Educational content assigned to patient", { ns: "common" }),
    category: "data",
  },
  {
    type: "purchase-list",
    label: i18n.t("Purchases", { ns: "common" }),
    description: i18n.t("List of purchases and payments", { ns: "common" }),
    category: "data",
  },
  {
    type: "file-upload",
    label: i18n.t("File Upload", { ns: "common" }),
    description: i18n.t("Button to upload files", { ns: "common" }),
    category: "data",
  },
  {
    type: "vital-data",
    label: i18n.t("Vital Data", { ns: "common" }),
    description: i18n.t("Lab results and vital sign readings", { ns: "common" }),
    category: "data",
  },
  {
    type: "weight-macro-tracker",
    label: i18n.t("Weight & Macro Tracker", { ns: "common" }),
    description: i18n.t("Log weight, food, and water with progress toward daily targets", { ns: "common" }),
    category: "data",
  },
  // Integration blocks
  {
    type: "stripe-subscriptions",
    label: i18n.t("Stripe Subscriptions", { ns: "common" }),
    description: i18n.t("Display and manage Stripe subscriptions", { ns: "common" }),
    category: "integrations",
  },
  {
    type: "stripe-billing-portal-button",
    label: i18n.t("Stripe Billing Portal", { ns: "common" }),
    description: i18n.t(
      "Redirects endusers to the Stripe self-serve billing portal for additional subscription management actions",
      { ns: "common" }
    ),
    category: "integrations",
  },
  {
    type: "stripe-invoices",
    label: i18n.t("Stripe Invoices", { ns: "common" }),
    description: i18n.t("Display patient invoice history from Stripe", { ns: "common" }),
    category: "integrations",
  },
  {
    type: "iframe",
    label: i18n.t("Iframe", { ns: "common" }),
    description: i18n.t("Embed external content with authenticated token passthrough", { ns: "common" }),
    category: "integrations",
  },
];
